import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText
} from './ThemeLayoutHelpers';

export default function MidnightLayout({ p }) {
  const T = p.storeTheme || {};
 
  const copy = getHeroContent(p, {
    eyebrow: "Midnight Edition",
    title: "Dark luxury for modern brands",
    subtitle: "Premium products presented in an immersive dark environment with subtle glow effects.",
    primaryCta: "Explore Collection",
    secondaryCta: "Discover More",
  });
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#0f0f1a] via-[#1a1a2e] to-[#0f0f1a] text-gray-200" style={{ fontFamily: T.fontFamily || "Inter, sans-serif" }}>
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-600/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-600/20 rounded-full blur-[100px]" />
      </div>
      
      {/* Top Bar */}
      <div className="relative border-b border-white/10 bg-black/40 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-xs">
          <span className="text-purple-400 font-mono">✦ PREMIUM SELECTION ✦</span>
          <div className="flex gap-4">
            {p.previewMode ? (
              <span className="text-purple-400">{p.storeInfo?.phone}</span>
            ) : (
              <a href={`tel:${p.storeInfo?.phone}`} className="hover:text-purple-400 transition">{p.storeInfo?.phone}</a>
            )}
            <span className="text-white/40">|</span>
            <span className="text-white/60">{p.storeInfo?.city}, {p.storeInfo?.state}</span>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <StoreLogo storeInfo={p.storeInfo} border="border-2 border-purple-500/50" />
          <span className="font-bold text-lg tracking-tight bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">{p.storeInfo?.businessName}</span>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="flex-1 max-w-md ml-4" />
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full border border-white/20 hover:bg-white/10 transition group">
              <FaHeart className="text-gray-300 group-hover:text-purple-400 transition" />
              {p.wishlistCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] flex items-center justify-center">{p.wishlistCount}</span>}
            </button>
            <CartBtn p={p} dark />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative">
        <HeroImage p={p} themeId="midnight" className="h-[500px]">
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f1a] via-transparent to-transparent pointer-events-none" />
        </HeroImage>
        <div className="absolute inset-0 max-w-7xl mx-auto px-4 flex flex-col justify-center">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-500/30 bg-purple-500/10 backdrop-blur-sm mb-6">
              <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
              <span className="text-xs font-mono tracking-wider text-purple-300"><InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText></span>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold tracking-tight bg-gradient-to-r from-white via-purple-200 to-purple-400 bg-clip-text text-transparent"><InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText></h1>
            <p className="mt-4 text-gray-400 max-w-lg"><InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText></p>
            <div className="mt-8 flex gap-3">
              <button onClick={() => p.setActiveTab("products")} className="px-6 py-3 rounded-full font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-purple-500/25 transition-all"><InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText></button>
              <button onClick={() => p.setActiveTab("details")} className="px-6 py-3 rounded-full font-semibold border border-white/20 hover:bg-white/10 transition"><InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText></button>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto w-full px-4 mt-8">
        <div className="flex gap-1 p-1 rounded-full bg-white/5 border border-white/10 w-fit">
          {[["products", getTabLabel(p, "products")], ["services", getTabLabel(p, "services")], ["details", getTabLabel(p, "details")]].map(([k, l]) => (
            <button key={k} onClick={() => p.setActiveTab(k)} className={`px-6 py-2 rounded-full text-sm font-semibold transition-all ${p.activeTab === k ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg" : "text-gray-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <TabContent p={p} dark />
      </section>

      <StoreFooter p={p} dark />
    </div>
  );
}